import { buildLookupSearchUrl } from '../components/funnelback-search-form/build-lookup-search-url';
import type { SearchKeyValueParam } from '../types';

/** Maximum length for a Funnelback search query string. */
export const SEARCH_MAX_QUERY_LENGTH = 256;

// eslint-disable-next-line no-control-regex
const CONTROL_CHAR_PATTERN = /[\0-\x1F\x7F]/g;

/**
 * Strip unsafe characters and cap length while the user is typing.
 * Preserves internal and trailing spaces so multi-word queries can be entered.
 */
export function sanitizeFunnelbackSearchInput(raw: string): string {
  return raw
    .replace(CONTROL_CHAR_PATTERN, '')
    .slice(0, SEARCH_MAX_QUERY_LENGTH);
}

/**
 * Normalize user-supplied search text before Funnelback requests or URL hydration.
 */
export function sanitizeFunnelbackSearchQuery(raw: string): string {
  return sanitizeFunnelbackSearchInput(raw).trim();
}

function resolveLookupUrl(raw: string, origin: string): URL | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      return new URL(trimmed);
    }
    return new URL(trimmed, origin);
  } catch {
    return null;
  }
}

function hasUserinfo(url: URL): boolean {
  return Boolean(url.username || url.password);
}

function isAllowedFunnelbackProtocol(url: URL): boolean {
  if (url.protocol === 'https:') {
    return true;
  }
  if (url.protocol === 'http:') {
    const host = url.hostname;
    return host === 'localhost' || host === '127.0.0.1';
  }
  return false;
}

/**
 * Ensure a built Funnelback lookup URL targets the same origin and pathname as the
 * configured base URL. User input may only change query parameters.
 */
export function assertSafeFunnelbackFetchUrl(
  builtUrl: string,
  lookupBaseUrl: string,
  origin = typeof window !== 'undefined'
    ? window.location.origin
    : 'https://localhost',
): string | null {
  const base = resolveLookupUrl(lookupBaseUrl, origin);
  const built = resolveLookupUrl(builtUrl, origin);
  if (!base || !built) {
    return null;
  }
  if (
    !isAllowedFunnelbackProtocol(base) ||
    !isAllowedFunnelbackProtocol(built)
  ) {
    return null;
  }
  if (hasUserinfo(base) || hasUserinfo(built)) {
    return null;
  }
  if (built.origin !== base.origin || built.pathname !== base.pathname) {
    return null;
  }
  return built.toString();
}

/**
 * Build a Funnelback suggest/autocomplete URL from a base path and extra query pairs.
 */
export function buildFunnelbackSuggestUrl(
  suggestBaseUrl: string,
  partialQuery: string,
  extraParams?: SearchKeyValueParam[],
): string {
  const base = suggestBaseUrl.trim();
  if (!base) {
    return '';
  }
  const params = new URLSearchParams();
  for (const { key, value } of extraParams ?? []) {
    params.append(key, value);
  }
  params.set('partial_query', sanitizeFunnelbackSearchQuery(partialQuery));
  const join = base.includes('?') ? '&' : '?';
  return `${base}${join}${params.toString()}`;
}

/**
 * Build and validate a Funnelback suggest fetch URL. Returns null when validation fails.
 */
export function buildSafeFunnelbackSuggestFetchUrl(
  suggestBaseUrl: string,
  partialQuery: string,
  extraParams?: SearchKeyValueParam[],
): string | null {
  const trimmedBase = suggestBaseUrl.trim();
  if (!trimmedBase) {
    return null;
  }
  const built = buildFunnelbackSuggestUrl(
    trimmedBase,
    partialQuery,
    extraParams,
  );
  return assertSafeFunnelbackFetchUrl(built, trimmedBase);
}

/**
 * Build and validate a Funnelback lookup fetch URL. Returns null when validation fails.
 */
export function buildSafeFunnelbackLookupFetchUrl(
  lookupBaseUrl: string,
  query: string,
  extraParams?: SearchKeyValueParam[],
): string | null {
  const sanitizedQuery = sanitizeFunnelbackSearchQuery(query);
  const built = buildLookupSearchUrl(
    lookupBaseUrl,
    sanitizedQuery,
    extraParams,
  );
  return assertSafeFunnelbackFetchUrl(built, lookupBaseUrl);
}
